import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HttpError } from '../../../shared/api/httpClient';
import { useSessionStore, selectToken } from '../state/sessionStore';
import { adaptTokenCheckResponse } from '../../../shared/api/intelliSightAdapters';
import { requestWithAuthRetry } from '../../auth/api/authenticatedRequest';

const DEFAULT_INTERVAL = Number(import.meta.env.VITE_QUOTA_POLL_INTERVAL_MS ?? 60_000);

export function useQuotaSync(intervalMs: number = DEFAULT_INTERVAL) {
  const { t } = useTranslation();
  const token = useSessionStore(selectToken);
  const updateQuota = useSessionStore((state) => state.updateQuota);
  const setQuotaStatus = useSessionStore((state) => state.setQuotaStatus);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const intervalRef = useRef<number | null>(null);
  const syncInFlightRef = useRef<Promise<void> | null>(null);
  const latestTokenRef = useRef(token);
  const sessionExpiredRef = useRef(false);
  const tokenValue = token?.token ?? null;
  const tokenType = token?.tokenType;

  useEffect(() => {
    latestTokenRef.current = token;
  }, [token]);

  const performSync = useCallback(async () => {
    const activeToken = latestTokenRef.current;
    if (!activeToken || syncInFlightRef.current) {
      return syncInFlightRef.current ?? Promise.resolve();
    }

    const syncPromise = (async () => {
      setIsSyncing(true);
      try {
        const response = await requestWithAuthRetry('GET', '/api/v1/tokens/check', {
          authToken: activeToken,
          retryLabel: 'quota-sync'
        });
        const quota = adaptTokenCheckResponse(response, activeToken.quota.total);
        updateQuota(quota);
        setQuotaStatus('ready');
        setError(null);
      } catch (err) {
        setQuotaStatus('error');
        const normalizedError = err instanceof Error ? err : new Error('Failed to sync quota');

        if (err instanceof HttpError && err.status === 401) {
          if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          latestTokenRef.current = null;

          if (activeToken.tokenType === 'user') {
            if (!sessionExpiredRef.current) {
              sessionExpiredRef.current = true;
              const message = t('app.notifications.sessionExpired');
              const sessionError = new Error(message);
              setError(sessionError);
              console.warn('[quota-sync] session expired for user token; polling halted');
            }
            return;
          }

          if (activeToken.tokenType === 'device') {
            console.warn('[quota-sync] device token unauthorized; polling halted');
            setError(normalizedError);
            return;
          }
        }

        console.warn('[quota-sync] failed to refresh quota', normalizedError);
        setError(normalizedError);
      } finally {
        setIsSyncing(false);
        syncInFlightRef.current = null;
      }
    })();

    syncInFlightRef.current = syncPromise;
    return syncPromise;
  }, [t, updateQuota, setQuotaStatus]);

  useEffect(() => {
    if (!tokenValue || (tokenType !== 'user' && tokenType !== 'device')) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    void performSync();

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }

    intervalRef.current = window.setInterval(() => {
      void performSync();
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [tokenType, tokenValue, intervalMs, performSync]);

  return {
    isSyncing,
    error,
    syncNow: performSync
  };
}
